import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { stopRecovery } from '../lib/recovery/recoveryApi';
import './recovery.css';

/**
 * "Stop this" — the page behind the link in every recovery notice.
 *
 * The audience is an advisor or a guardian who has just read an alarming
 * email, possibly on a phone, possibly at night, and who has no account with
 * us at all. So: no sign-in, one button, and the button is the destructive-
 * looking one because stopping is the safe direction. If they are wrong and
 * the owner really did lose their phone, the owner simply asks again.
 */
export default function RecoveryStopScreen() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function stop() {
    setBusy(true);
    try {
      await stopRecovery(token, 'Stopped from the emailed link');
    } catch {
      // Same page either way. A stop link that reported "already used" for
      // real tokens and "invalid" for guesses would be a way to test tokens.
    } finally {
      setBusy(false);
      setDone(true);
    }
  }

  if (token === '') {
    return (
      <section className="auth recovery">
        <h1 className="vault__title">That link is incomplete</h1>
        <p className="vault__sub">
          Open the link from the email again, in full. If you are worried about the vault, contact
          the advisor named in that email.
        </p>
      </section>
    );
  }

  if (done) {
    return (
      <section className="auth recovery">
        <p className="vault__kicker">Thank you</p>
        <h1 className="vault__title">That is stopped</h1>
        <p className="vault__sub">
          If a recovery was running, it has been cancelled and nobody will be able to use it. The
          vault is untouched.
        </p>
        <p className="vault__sub">
          If this was genuinely the owner losing their phone, they can simply ask again — and they
          can reach their advisor to be verified on a video call instead of waiting.
        </p>
      </section>
    );
  }

  return (
    <section className="auth recovery">
      <p className="vault__kicker">Someone asked to get back in</p>
      <h1 className="vault__title">Was this expected?</h1>
      <p className="vault__sub">
        Someone has asked to get back into a Legacy Architect vault after losing their phone. If
        that is expected, you do not need to do anything at all — it will go ahead in three days.
      </p>
      <p className="vault__sub">
        If it is not expected, stop it. Nothing is lost by stopping something that turns out to be
        genuine; the person can simply ask again.
      </p>
      <button type="button" className="la-btn-primary" onClick={() => void stop()} disabled={busy}>
        {busy ? 'Stopping…' : 'Stop this now'}
      </button>
      <p className="recovery__reassure">
        You do not need an account or a password to use this button.
      </p>
    </section>
  );
}
